import React from 'react';
import { Box, useTheme } from '@mui/material';
import { animations } from '../theme/animations';

interface EnergyDecorProps {
  variant?: 'sun' | 'battery' | 'lightning' | 'wind';
  size?: number;
  animated?: boolean;
}

export const EnergyDecor: React.FC<EnergyDecorProps> = ({
  variant = 'sun',
  size = 100,
  animated = true
}) => {
  const theme = useTheme();
  const primaryColor = theme.palette.mode === 'dark' ? '#ffd700' : '#1d388d';
  const secondaryColor = theme.palette.mode === 'dark' ? '#ffed4e' : '#4a90e2';

  const icons = {
    sun: (
      <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="sun-gradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={primaryColor} stopOpacity="1" />
            <stop offset="100%" stopColor={secondaryColor} stopOpacity="0.6" />
          </radialGradient>
        </defs>
        <circle cx="50" cy="50" r="20" fill="url(#sun-gradient)" />
        {[...Array(8)].map((_, i) => {
          const angle = (i * 45 * Math.PI) / 180;
          const x1 = 50 + Math.cos(angle) * 25;
          const y1 = 50 + Math.sin(angle) * 25;
          const x2 = 50 + Math.cos(angle) * 35;
          const y2 = 50 + Math.sin(angle) * 35;
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={primaryColor}
              strokeWidth="3"
              strokeLinecap="round"
              opacity="0.8"
            />
          );
        })}
      </svg>
    ),

    battery: (
      <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="battery-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={primaryColor} stopOpacity="1" />
            <stop offset="100%" stopColor={secondaryColor} stopOpacity="0.8" />
          </linearGradient>
        </defs>
        <rect x="25" y="30" width="50" height="50" rx="5" fill="none" stroke={primaryColor} strokeWidth="3" />
        <rect x="42" y="22" width="16" height="8" rx="2" fill={primaryColor} />
        <rect x="30" y="50" width="40" height="25" fill="url(#battery-gradient)" opacity="0.8" />
        <path d="M 45 40 L 50 50 L 45 50 L 50 60" fill="none" stroke={secondaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),

    lightning: (
      <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="lightning-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={primaryColor} />
            <stop offset="100%" stopColor={secondaryColor} />
          </linearGradient>
        </defs>
        <path
          d="M 55 20 L 35 50 L 45 50 L 30 80 L 60 45 L 50 45 L 65 20 Z"
          fill="url(#lightning-gradient)"
          stroke={primaryColor}
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    ),

    wind: (
      <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="wind-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={primaryColor} stopOpacity="0.3" />
            <stop offset="50%" stopColor={secondaryColor} stopOpacity="1" />
            <stop offset="100%" stopColor={primaryColor} stopOpacity="0.3" />
          </linearGradient>
        </defs>
        <path d="M 20 30 Q 40 25, 60 30" fill="none" stroke="url(#wind-gradient)" strokeWidth="3" strokeLinecap="round" />
        <path d="M 25 45 Q 45 40, 70 45" fill="none" stroke="url(#wind-gradient)" strokeWidth="3" strokeLinecap="round" />
        <path d="M 20 60 Q 45 55, 65 60" fill="none" stroke="url(#wind-gradient)" strokeWidth="3" strokeLinecap="round" />
      </svg>
    ),
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(animated && variant === 'sun' && {
          animation: animations.rotate,
        }),
        ...(animated && variant === 'battery' && {
          animation: animations.pulse,
        }),
        ...(animated && variant === 'lightning' && {
          animation: animations.glow,
        }),
        ...(animated && variant === 'wind' && {
          animation: animations.float,
        }),
      }}
    >
      {icons[variant]}
    </Box>
  );
};

